import {Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {
  ProductPackageData,
  ProductPackageDialogData,
  ProductPackageFormField
} from "./types/product-package-modal.types";
import {FormGroup} from "@angular/forms";
import {
  productPackageResourceType
} from "./configs/product-package-modal-config";
import {ProductPackageModalService} from "./services/product-package-modal.service";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {ProductPackageFilesStateService} from "./services/product-package-files-state.service";
import {defaultProductPackageData} from "./configs/default-product-package";
import {productPackageFormFields} from "./configs/product-package-form-fields";
import {GetDataInModalParams} from "../../../types/product-card-modals.types";
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MatButton} from "@angular/material/button";
import {LoadingService} from "../../../../../../core/services/loading.service";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {ProductPackageFormComponent} from "./components/product-package-form/product-package-form.component";
import {ProductPackageFilesComponent} from "./components/product-package-files/product-package-files.component";
import {
  ProductPackageAccordionTitleComponent
} from "./components/product-package-accordion-title/product-package-accordion-title.component";
import {ProductPackageSelectDataStateService} from "./services/product-package-select-data-state.service";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-product-package-modal',
  imports: [
    BaseModalComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatButton,
    ProductPackageFormComponent,
    ProductPackageFilesComponent,
    ProductPackageAccordionTitleComponent,
    LoaderComponent
  ],
  templateUrl: './product-package-modal.component.html',
  styleUrl: './product-package-modal.component.scss',
  providers: [ LoadingService, EditModeService ]
})
export class ProductPackageModalComponent extends BaseModalComponent {
  public productPackageDialogData = inject<ProductPackageDialogData>(MAT_DIALOG_DATA);
  private productPackageModalService: ProductPackageModalService = inject(ProductPackageModalService);
  private productPackageFilesStateService: ProductPackageFilesStateService = inject(ProductPackageFilesStateService);
  private productPackageSelectDataStateService: ProductPackageSelectDataStateService = inject(ProductPackageSelectDataStateService);

  public productPackageFormGroups: Array<FormGroup> = [];
  public productPackagesData: Array<ProductPackageData> = [];
  public isValid: boolean = false;
  public noFirstData: boolean = false;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(productPackageResourceType.key);
  }

  public async handelImportPackages(): Promise<void> {
    const result = await this.modalsService.openTechnicalParameterModal(this.productPackageDialogData.productPackages, 'importProductPackages');
    result === 'success' && await this.initComponent();
  }

  public handleAddNew() {
    const formGroup = this.baseModalService.getFormGroup<ProductPackageFormField>(productPackageFormFields);
    this.productPackageFormGroups.push(formGroup);
    this.productPackagesData.push(defaultProductPackageData);
    this.isValid = false;
  }

  public updateProductPackageData(data: ProductPackageData, index: number): void {
    this.productPackagesData[index] = data;
    this.productPackageFormGroups.at(index)?.patchValue(data);
    this.isValid = !!data.id;
    this.noFirstData = !!this.productPackagesData.length;
  }

  public deleteProductPackage(index: number): void {
    this.productPackageFormGroups.splice(index, 1);
    this.productPackagesData.splice(index, 1);
  }

  public async handleDelete(id: number, index: number): Promise<void> {
    await this.deleteRecord(productPackageResourceType.path, id)
      .then(() => this.removeProductPackageAtIndex(index, id));
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(productPackageResourceType.key);
    this.loadingService.setIsLoadingState(true);
    await this.productPackageSelectDataStateService.fetchSelectData(this.productPackageDialogData.parentId);
    await this.fetchProductPackageData();
    this.isValid = !!this.productPackagesData.at(0)?.id;
    this.noFirstData = !!this.productPackagesData.at(0)?.id;
    this.loadingService.setIsLoadingState(false);
  }

  private async fetchProductPackageData(): Promise<void> {
    const params: GetDataInModalParams = {
      path: productPackageResourceType.path,
      queryParams: {
        technicalParametersId: Number(this.productPackageDialogData.productPackages.id)
      }
    };

    this.productPackagesData = await this.productPackageModalService.getDataInModal<ProductPackageData>(params).catch(() => []);
    this.initForm();
  }

  private initForm(): void {
    if (!this.productPackagesData.length) {
      this.handleAddNew();
      return;
    }

    this.productPackageFormGroups = this.productPackagesData.map(data => {
      const formGroup = this.baseModalService.getFormGroup<ProductPackageFormField>(productPackageFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });
  }

  private removeProductPackageAtIndex(index: number, id: number): void {
    this.productPackageFormGroups.splice(index, 1);
    this.productPackagesData.splice(index, 1);
    this.productPackageFilesStateService.deleteProductPackageFilesState(id);

    if (!this.productPackagesData.length) {
      this.noFirstData = false;
      this.handleAddNew();
    }
  }
}
